import { useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import { Oval } from "react-loader-spinner";

import { formatUSPhone } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import MultiSelectChipInput from "src/Pages/Setting/Report/components/MultiSelectChipInput";

function AdminAddModal({ customer, selectedAccount, onAddClicked, onCancelClicked }) {
  const currentUser = UserService.getCurrentUser();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [locationIds, setLocationIds] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // The admin will only manage the locations selected here.
  const locationOptions = useMemo(
    () =>
      (selectedAccount?.customers || [])
        .filter((location) => !location.isArchived)
        .map((location) => ({ id: location.id, name: location.companyName })),
    [selectedAccount]
  );

  const nameError = submitted && !name.trim();
  const emailError = submitted && !email.trim();
  const locationError = submitted && locationIds.length === 0;

  const addClicked = async () => {
    if (isInviting) return;
    setSubmitted(true);
    if (!name.trim() || !email.trim() || locationIds.length === 0) return;

    const data = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone,
      accountId: selectedAccount?.id,
      customerId: customer?.id,
      customerLocationIds: locationIds,
      invitedById: currentUser.id,
      company: currentUser.company,
    };
    setIsInviting(true);
    try {
      await onAddClicked(data);
    } finally {
      setIsInviting(false);
    }
  };

  const cancelClicked = () => {
    if (isInviting) return;
    onCancelClicked();
  };

  return (
    <div
      onClick={cancelClicked}
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-tour="invite-form"
        className="w-[672px] max-w-full max-h-[90vh] bg-white rounded-[12px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E8F3]">
          <div className="flex flex-col">
            <h2 className="text-[20px] leading-[28px] capitalize font-OpenSansSemiBold text-[#1A2332]">
              Invite Customer Admin
            </h2>
            <p className="text-[14px] leading-[20px] font-OpenSansRegular text-[#868DA6]">
              Invite an admin to the customer app and choose the locations they manage.
            </p>
          </div>
          <FiX size={18} className="text-[#868DA6] cursor-pointer" onClick={cancelClicked} />
        </div>

        <div className="p-8 overflow-y-auto flex-1">
          <div className="flex flex-col gap-4">
            <Field
              label="Name"
              required
              value={name}
              onChange={setName}
              placeholder="Enter name"
              error={nameError}
            />
            <Field
              label="Email"
              required
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="Enter email"
              error={emailError}
            />
            <Field
              label="Phone Number"
              value={phone}
              onChange={(v) => setPhone(formatUSPhone(v))}
              placeholder="(xxx) xxx-xxxx"
            />
            <div className="flex flex-col gap-1" data-tour="invite-locations">
              <MultiSelectChipInput
                label="Locations"
                required
                error={locationError}
                options={locationOptions}
                selectedIds={locationIds}
                onChange={setLocationIds}
                placeholder="Search locations..."
              />
              {locationError && (
                <p className="text-[12px] text-red-500 font-OpenSansRegular">
                  Select at least one location for this admin.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
          <button
            type="button"
            data-tour="invite-cancel"
            onClick={cancelClicked}
            className={`h-[40px] px-1 rounded-[6px] text-[16px] leading-[24px] capitalize font-OpenSansSemiBold text-[#7C829A] ${
              isInviting ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            data-tour="invite-submit"
            onClick={addClicked}
            className={`h-[40px] px-4 rounded-[8px] text-[16px] leading-[24px] capitalize font-OpenSansSemiBold bg-blueColor text-white flex items-center justify-center ${
              isInviting ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {isInviting ? (
              <Oval
                visible={true}
                height="18"
                width="18"
                color="#FFFFFF"
                strokeWidth={8}
                secondaryColor="#FFEEFF"
                ariaLabel="invite-loading"
              />
            ) : (
              "Send Invite"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, type = "text", error }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="block text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-[40px] px-3 rounded-[6px] border text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#1A2332] focus:border-blueColor ${
          error ? "border-red-500" : "border-[#E5E8F3]"
        }`}
      />
    </div>
  );
}

export default AdminAddModal;
